import { useState } from "react";
import { motion } from "framer-motion";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Calculator, TrendingUp, DollarSign, Clock } from "lucide-react";

const ROICalculator = () => {
  const [callVolume, setCallVolume] = useState<number>(1000);
  const [agentCost, setAgentCost] = useState<number>(15);
  const [avgHandleTime, setAvgHandleTime] = useState<number>(8);

  // Calculations
  const monthlyAgentCost = callVolume * agentCost;
  const aiCostPerCall = 0.5; // $0.50 per AI call
  const monthlyAICost = callVolume * aiCostPerCall;
  const monthlySavings = monthlyAgentCost - monthlyAICost;
  const annualSavings = monthlySavings * 12;
  const percentageSavings = ((monthlySavings / monthlyAgentCost) * 100).toFixed(1);
  const timePerMonth = (callVolume * avgHandleTime) / 60;
  const timeSavedPercent = 60; // AI handles 60% of calls

  return (
    <Card className="p-8 md:p-12 glass-effect border-primary/30 bg-gradient-to-br from-primary/5 to-accent/5">
      <div className="flex items-center gap-3 mb-8">
        <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary to-accent flex items-center justify-center">
          <Calculator className="w-8 h-8 text-white" />
        </div>
        <div>
          <h3 className="text-3xl font-bold">ROI Calculator</h3>
          <p className="text-muted-foreground">Calculate your potential savings</p>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-6 mb-8">
        <div>
          <label className="block text-sm font-semibold mb-2">Monthly Call Volume</label>
          <Input
            type="number"
            value={callVolume}
            onChange={(e) => setCallVolume(Number(e.target.value))}
            className="text-lg"
          />
        </div>
        <div>
          <label className="block text-sm font-semibold mb-2">Cost per Agent Call ($)</label>
          <Input
            type="number"
            value={agentCost}
            onChange={(e) => setAgentCost(Number(e.target.value))}
            className="text-lg"
          />
        </div>
        <div>
          <label className="block text-sm font-semibold mb-2">Avg Handle Time (min)</label>
          <Input
            type="number"
            value={avgHandleTime}
            onChange={(e) => setAvgHandleTime(Number(e.target.value))}
            className="text-lg"
          />
        </div>
      </div>

      <div className="grid md:grid-cols-4 gap-4 mb-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="p-6 rounded-xl bg-gradient-to-br from-green-500/20 to-emerald-500/20 border border-green-500/30"
        >
          <DollarSign className="w-8 h-8 text-green-400 mb-3" />
          <div className="text-3xl font-bold text-green-400 mb-1">
            ${monthlySavings.toLocaleString()}
          </div>
          <div className="text-sm text-muted-foreground">Monthly Savings</div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
          className="p-6 rounded-xl bg-gradient-to-br from-blue-500/20 to-cyan-500/20 border border-blue-500/30"
        >
          <TrendingUp className="w-8 h-8 text-blue-400 mb-3" />
          <div className="text-3xl font-bold text-blue-400 mb-1">
            ${annualSavings.toLocaleString()}
          </div>
          <div className="text-sm text-muted-foreground">Annual Savings</div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.2 }}
          className="p-6 rounded-xl bg-gradient-to-br from-purple-500/20 to-pink-500/20 border border-purple-500/30"
        >
          <TrendingUp className="w-8 h-8 text-purple-400 mb-3" />
          <div className="text-3xl font-bold text-purple-400 mb-1">{percentageSavings}%</div>
          <div className="text-sm text-muted-foreground">Cost Reduction</div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.3 }}
          className="p-6 rounded-xl bg-gradient-to-br from-amber-500/20 to-orange-500/20 border border-amber-500/30"
        >
          <Clock className="w-8 h-8 text-amber-400 mb-3" />
          <div className="text-3xl font-bold text-amber-400 mb-1">{timePerMonth.toFixed(0)}h</div>
          <div className="text-sm text-muted-foreground">Hours Saved/Month</div>
        </motion.div>
      </div>

      <div className="bg-gradient-to-r from-primary/10 to-accent/10 rounded-xl p-6 mb-6">
        <p className="text-center text-lg">
          <span className="font-bold text-primary">AI Efficiency:</span> At ${aiCostPerCall}/call vs ${agentCost}/call agent cost, 
          you save <span className="font-bold gradient-brand-text">{percentageSavings}%</span> while handling{" "}
          <span className="font-bold text-accent">{timeSavedPercent}%</span> of calls automatically
        </p>
      </div>

      <Button className="w-full gradient-brand text-white hover:opacity-90 text-lg py-6">
        Get Detailed ROI Report →
      </Button>
    </Card>
  );
};

export default ROICalculator;
